import * as Ariakit from '@ariakit/react'
import * as React from 'react'
import { useInRouterContext } from 'react-router'

import { ComboBox, ComboBoxProps } from '../combo-box'
import { IconNameType } from '../icon'
import { SearchListItem } from './constants'
import { SearchError } from './search-error'
import { SearchList } from './search-list'
import {
  SearchNavigationWrapperInRouterContext,
  SearchNavigationWrapperOutsideRouterContext,
} from './search-navigation-wrapper'

export interface SearchProps
  extends Pick<
    ComboBoxProps,
    | 'state'
    | 'hasData'
    | 'autoFocus'
    | 'inputProps'
    | 'inputComponent'
    | 'popoverProps'
    | 'aria-label'
    | 'inputTestId'
    | 'getInputValue'
  > {
  /**
   * Array of items to display in the search list
   */
  items?: SearchListItem[]

  /**
   * Minimum length of input value before showing results
   *
   * @default 1
   */
  minValueLength?: number

  /**
   * Placeholder text for the search input
   */
  placeholder?: string

  /**
   * Content to show on the right side of the input (icon or custom element)
   */
  rightContent?: React.JSX.Element | IconNameType

  /**
   * Custom footer content shown at the bottom of the search results
   */
  footer?: React.ReactNode

  /**
   * Whether to show the search icon in the input
   *
   * @default true
   */
  hasSearchIcon?: boolean

  /**
   * Whether to match the width of the input and its dropdown box
   */
  isMatchInputAndBoxWidth?: boolean

  /**
   * Whether the search is currently loading results
   *
   * @default false
   */
  isLoading?: boolean | null

  /**
   * Whether there was an error loading the search results. When passed a string
   * it’s shown as the error text
   *
   * @default false
   */
  isError?: string | boolean | null

  /**
   * Callback function to retry when there's an error
   */
  onErrorRetry?: () => void
}

export function Search({
  state,
  children,
  items,
  hasData,
  footer,
  'aria-label': ariaLabel,
  inputProps,
  popoverProps,
  inputTestId,
  minValueLength = 1,
  hasSearchIcon = true,
  isLoading = false,
  isError = false,
  onErrorRetry,
  inputComponent,
  ...props
}: React.PropsWithChildren<SearchProps>) {
  const isInRouterContext = useInRouterContext()
  const isMounted = Ariakit.useStoreState(state, 'mounted')

  const Wrapper = isInRouterContext
    ? SearchNavigationWrapperInRouterContext
    : SearchNavigationWrapperOutsideRouterContext

  return (
    <Wrapper state={state} items={items}>
      <ComboBox
        state={state}
        minValueLength={minValueLength}
        aria-label={ariaLabel}
        autoFocus={props.autoFocus}
        hasData={hasData}
        inputProps={{
          autoComplete: 'off',
          autoCorrect: 'off',
          autoCapitalize: 'off',
          onBlur: (e) => {
            // prevent combobox from setting input value on input blur
            e.preventDefault()
          },
          leftContent: hasSearchIcon ? 'search' : undefined,
          ...inputProps,
        }}
        popoverProps={{
          sameWidth: props.isMatchInputAndBoxWidth,
          fitViewport: !props.isMatchInputAndBoxWidth,
          ...popoverProps,
        }}
        inputTestId={inputTestId}
        getInputValue={props.getInputValue}
        inputComponent={inputComponent}
      >
        {isMounted &&
          (isError ? (
            <SearchError onRetry={onErrorRetry} errorText={typeof isError === 'string' ? isError : undefined} />
          ) : (
            <SearchList state={state} footer={footer} items={items} isLoading={isLoading!}>
              {children}
            </SearchList>
          ))}
      </ComboBox>
    </Wrapper>
  )
}
